import { ApolloClient } from "@apollo/client";
import { QueryWorkItemCounts, QueryWorkItemsForProject, QueryWorkItemsForProjectPerWorker } from "../../../queries/QueryWorkitems";
import { WorkitemStatus, CollectionWithWorkItemCounts } from "./types";

export interface WorkItemsForProject {
    data?: {
        QueryWorkItems: {
            data: {
                completed_workitems: number;
                in_progress_workitems: number;
                approved_workitems: number;
                closed_workitems: number;
                audit_workitems?: number;
                audit_complete_workitems?: number;
                trap_complete_workitems?: number;
                qa_collection_id: string;
            }[]
        }
    }
}

// Handle case where data might be a JSON string that needs parsing
const parseWorkItemData = (data: any) => {
    if (typeof data === 'string') {
        try {
            return JSON.parse(data);
        } catch (parseError) {
            console.error('Error parsing QueryWorkItems data as JSON:', parseError);
        }
    }
    return data;
};

export const fetchProjectsWithCounts = async (
    collectionIds: string[],
    collections: CollectionWithWorkItemCounts[],
    activeContextRoles: string[],
    workerId: string,
    client: ApolloClient<any>,
    isPolling: boolean
) => {
    const isCQAManager = activeContextRoles.includes("cqa_manager");
    let responseWorkItemsForProjects: WorkItemsForProject = { data: { QueryWorkItems: { data: [] } } };

    if (isCQAManager) {
        responseWorkItemsForProjects = await client.mutate({
            mutation: QueryWorkItemCounts,
            variables: {
                workItemQuery: {
                    query: QueryWorkItemsForProject(collectionIds)
                }
            }
        });
    }

    const responseworkItemsForProjectsPerWorker: WorkItemsForProject = await client.mutate({
        mutation: QueryWorkItemCounts,
        variables: {
            workItemQuery: {
                query: QueryWorkItemsForProjectPerWorker(collectionIds, workerId)
            }
        }
    });

    let workItemDataForProjects = responseWorkItemsForProjects?.data?.QueryWorkItems?.data;
    let workItemDataForProjectsPerWorker = responseworkItemsForProjectsPerWorker?.data?.QueryWorkItems?.data;

    workItemDataForProjects = parseWorkItemData(workItemDataForProjects);
    workItemDataForProjectsPerWorker = parseWorkItemData(workItemDataForProjectsPerWorker);

    if (!Array.isArray(workItemDataForProjects)) {
        console.warn('QueryWorkItems.data is not an array:', workItemDataForProjects);
        workItemDataForProjects = [];
    }

    if (!Array.isArray(workItemDataForProjectsPerWorker)) {
        console.warn('QueryWorkItems.data is not an array:', workItemDataForProjectsPerWorker);
        workItemDataForProjectsPerWorker = [];
    }

    // Update projects with counts
    const projectsWithCounts = collections.map(project => {
        const matchingProject = workItemDataForProjects?.find((p: any) => p.qa_collection_id === project.collection_id);
        const matchingProjectPerWorker = workItemDataForProjectsPerWorker?.find((p: any) => p.qa_collection_id === project.collection_id);

        const assignedToMeCount = isCQAManager ? matchingProjectPerWorker?.in_progress_workitems || 0 : 0;
        const inCqaCount = isCQAManager ? matchingProject?.in_progress_workitems || 0 : matchingProjectPerWorker?.in_progress_workitems || 0;
        const cqaCompleteCount = isCQAManager ? matchingProject?.completed_workitems || 0 : matchingProjectPerWorker?.completed_workitems || 0;
        // Only include approved count for cqa_managers (cqa_coders can't see approved list)
        const cqaApprovedCount = isCQAManager ? matchingProject?.approved_workitems || 0 : 0;
        const cqaClosedCount = isCQAManager ? matchingProject?.closed_workitems || 0 : 0;
        const cqaAuditCount = isCQAManager ? matchingProject?.audit_workitems || 0 : 0;
        const cqaAuditCompleteCount = isCQAManager ? matchingProject?.audit_complete_workitems || 0 : 0;
        const cqaTrapCompleteCount = isCQAManager ? matchingProject?.trap_complete_workitems || 0 : 0;

        if (!isPolling && !matchingProject && !matchingProjectPerWorker) {
            console.warn(`No matching project found for collection_id: ${project.collection_id}`);
        }

        // Ensure filters are preserved when adding counts
        // The project should always have filters from createProjectItemFromQaRule
        const projectWithCounts: CollectionWithWorkItemCounts = {
            ...project,
            workItemCounts: (matchingProject || matchingProjectPerWorker) ? {
                [WorkitemStatus.ASSIGNED_TO_ME]: assignedToMeCount,
                [WorkitemStatus.IN_CQA]: inCqaCount,
                [WorkitemStatus.CQA_COMPLETE]: cqaCompleteCount,
                // Approved tab spans direct approvals + audit-graded items (same concept: manager-reviewed).
                [WorkitemStatus.CQA_APPROVED]: cqaApprovedCount + cqaAuditCompleteCount,
                [WorkitemStatus.CQA_CLOSED]: cqaClosedCount,
                // Audits tab shows only pending items within the active sample.
                [WorkitemStatus.CQA_AUDIT]: cqaAuditCount,
                [WorkitemStatus.CQA_AUDIT_COMPLETE]: cqaAuditCompleteCount,
                [WorkitemStatus.CQA_TRAP_COMPLETE]: cqaTrapCompleteCount,
            } : {
                [WorkitemStatus.ASSIGNED_TO_ME]: 0,
                [WorkitemStatus.IN_CQA]: 0,
                [WorkitemStatus.CQA_COMPLETE]: 0,
                [WorkitemStatus.CQA_APPROVED]: 0,
                [WorkitemStatus.CQA_CLOSED]: 0,
                [WorkitemStatus.CQA_AUDIT]: 0,
                [WorkitemStatus.CQA_AUDIT_COMPLETE]: 0,
                [WorkitemStatus.CQA_TRAP_COMPLETE]: 0,
            }
        };

        // Debug: Log if project is missing filters
        if (!projectWithCounts.filters || !Array.isArray(projectWithCounts.filters) || projectWithCounts.filters.length === 0) {
            console.warn(`Project ${projectWithCounts.collection_id} is missing filters after adding counts`);
        }

        return projectWithCounts;
    });

    return projectsWithCounts;
};